<template>
  <div class="tracking-link-picker">
    <div
      ref="control"
      class="tracking-link-picker__control"
      :class="{
        'tracking-link-picker__control--open': isOpen,
        'tracking-link-picker__control--error': hasError,
        'tracking-link-picker__control--disabled': disabled,
      }"
      role="combobox"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      data-entity="producer-trackingLinkPicker-input"
      data-test="producer-trackingLinkPicker-input"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.esc.prevent="close"
    >
      <template v-if="selectedLink">
        <span class="fas fa-link tracking-link-picker__icon tracking-link-picker__icon--link" />

        <span class="tracking-link-picker__selected">
          <span
            class="tracking-link-picker__title"
            :title="selectedLink.description || 'Untitled link'"
          >
            {{ selectedLink.description || 'Untitled link' }}
          </span>
          <span class="tracking-link-picker__subtitle">{{ formatShortUrl(selectedLink) }}</span>
        </span>

        <span
          class="tracking-link-picker__tag"
          :style="typeTagStyle(selectedLink.type)"
        >
          {{ typeLabel(selectedLink.type) }}
        </span>
      </template>

      <template v-else>
        <span v-if="isLoading" class="tracking-link-picker__spinner" />
        <span v-else class="fas fa-magnifying-glass tracking-link-picker__icon" />

        <span
          class="tracking-link-picker__placeholder"
          :class="{ 'tracking-link-picker__placeholder--error': hasError }"
        >
          {{ placeholder }}
        </span>
      </template>

      <span
        class="fas fa-chevron-down tracking-link-picker__chevron"
        :class="{ 'tracking-link-picker__chevron--open': isOpen }"
      />
    </div>

    <p v-if="hasError" class="tracking-link-picker__error">
      <span class="fas fa-triangle-exclamation" />
      Could not load tracking links. Try again or contact support.
    </p>

    <div
      v-if="isOpen"
      class="tracking-link-picker__menu"
      data-entity="producer-trackingLinkPicker-menu"
    >
      <div class="tracking-link-picker__search-header">
        <div class="tracking-link-picker__search-wrapper">
          <span class="fas fa-magnifying-glass tracking-link-picker__search-icon" />
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="tracking-link-picker__search"
            :aria-activedescendant="
              highlightedLink ? `tracking-link-option-${highlightedLink.trackingLinkId}` : null
            "
            placeholder="Search by name or URL..."
            data-entity="producer-trackingLinkPicker-search"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="close(true)"
          />
        </div>
      </div>

      <div
        ref="menu"
        class="tracking-link-picker__results"
        role="listbox"
      >
        <div v-if="isLoading" class="tracking-link-picker__state">
          Loading tracking links...
        </div>

        <template v-else>
          <template v-if="pageLinks.length">
            <div class="tracking-link-picker__group-label">Pages</div>

            <button
              v-for="(link, index) in pageLinks"
              :id="`tracking-link-option-${link.trackingLinkId}`"
              :key="link.trackingLinkId"
              type="button"
              class="tracking-link-picker__option"
              :class="{ 'tracking-link-picker__option--highlighted': index === highlightedIndex }"
              role="option"
              :aria-selected="index === highlightedIndex"
              :title="link.description || 'Untitled link'"
              :data-entity="`producer-trackingLinkPicker-option-${link.trackingLinkId}`"
              @mouseenter="highlightedIndex = index"
              @focusin="highlightedIndex = index"
              @click="selectLink(link)"
            >
              <span class="tracking-link-picker__option-header">
                <span class="tracking-link-picker__title">
                  {{ link.description || 'Untitled link' }}
                </span>
                <span
                  class="tracking-link-picker__tag"
                  :style="typeTagStyle(link.type)"
                >
                  {{ typeLabel(link.type) }}
                </span>
              </span>
              <span
                class="tracking-link-picker__option-destination"
                :title="link.currentDestination?.destinationUrl"
              >
                {{ stripProtocol(link.currentDestination?.destinationUrl) }}
              </span>
              <span class="tracking-link-picker__subtitle">{{ formatShortUrl(link) }}</span>
            </button>
          </template>

          <template v-if="customLinks.length">
            <hr
              v-if="pageLinks.length"
              class="tracking-link-picker__group-divider"
            >

            <div class="tracking-link-picker__group-label">Links</div>

            <button
              v-for="(link, index) in customLinks"
              :id="`tracking-link-option-${link.trackingLinkId}`"
              :key="link.trackingLinkId"
              type="button"
              class="tracking-link-picker__option"
              :class="{
                'tracking-link-picker__option--highlighted':
                  pageLinks.length + index === highlightedIndex,
              }"
              role="option"
              :aria-selected="pageLinks.length + index === highlightedIndex"
              :title="link.description || 'Untitled link'"
              :data-entity="`producer-trackingLinkPicker-option-${link.trackingLinkId}`"
              @mouseenter="highlightedIndex = pageLinks.length + index"
              @focusin="highlightedIndex = pageLinks.length + index"
              @click="selectLink(link)"
            >
              <span class="tracking-link-picker__option-header">
                <span class="tracking-link-picker__title">
                  {{ link.description || 'Untitled link' }}
                </span>
                <span
                  class="tracking-link-picker__tag"
                  :style="typeTagStyle(link.type)"
                >
                  {{ typeLabel(link.type) }}
                </span>
              </span>
              <span
                class="tracking-link-picker__option-destination"
                :title="link.currentDestination?.destinationUrl"
              >
                {{ stripProtocol(link.currentDestination?.destinationUrl) }}
              </span>
              <span class="tracking-link-picker__subtitle">{{ formatShortUrl(link) }}</span>
            </button>
          </template>

          <div
            v-if="!hasError && !pageLinks.length && !customLinks.length"
            class="tracking-link-picker__state tracking-link-picker__empty"
          >
            <template v-if="search">
              <span class="fas fa-magnifying-glass tracking-link-picker__empty-icon" />
              <p class="tracking-link-picker__empty-title">No results for "{{ search }}"</p>
              <p class="tracking-link-picker__empty-subtitle">
                Try a different term or create a new link below.
              </p>
            </template>

            <template v-else>
              No tracking links found. Create one below.
            </template>
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<script>
import { getTrackingLinks, getTrackingLinkByRid } from '../../services/tracking-links';

const TRACKING_LINK_TYPE_STYLES = {
  custom: { backgroundColor: '#c2eff5', color: '#006775' },
  page: { backgroundColor: '#ffe6c2', color: '#8c5400' },
  shoppable: { backgroundColor: '#caf1d8', color: '#136c34' },
};

const FALLBACK_TYPE_STYLE = { backgroundColor: '#f1f5f9', color: '#334155' };

export default {
  name: 'TrackingLinkPicker',

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      links: [],
      isLoading: false,
      hasError: false,
      isOpen: false,
      search: '',
      loaded: false,
      highlightedIndex: -1,
    };
  },

  computed: {
    selectedLink() {
      return this.links.find((link) => link.rid === this.modelValue) || null;
    },

    filteredLinks() {
      const normalized = this.search.trim().toLowerCase();

      if (!normalized) {
        return this.links;
      }

      return this.links.filter((link) => (
        (link.description || '').toLowerCase().includes(normalized)
        || (link.currentDestination?.destinationUrl || '').toLowerCase().includes(normalized)
        || (link.rid || '').toLowerCase().includes(normalized)
      ));
    },

    pageLinks() {
      return this.filteredLinks.filter((link) => link.type === 'page');
    },

    customLinks() {
      return this.filteredLinks.filter((link) => (
        link.type !== 'page' && link.type !== 'shoppable'
      ));
    },

    // Flat, visual-order list used for keyboard navigation (Pages then Links).
    visibleLinks() {
      return [...this.pageLinks, ...this.customLinks];
    },

    highlightedLink() {
      return this.visibleLinks[this.highlightedIndex] || null;
    },

    placeholder() {
      if (this.hasError) {
        return 'Failed to load tracking links';
      }

      return this.isLoading ? 'Loading tracking links...' : 'Select a tracking link...';
    },

  },

  mounted() {
    document.addEventListener('click', this.onDocumentClick);

    // Resolve the selected link's title when one is already set.
    if (this.modelValue) {
      this.loadLinks();
    }
  },

  watch: {
    modelValue() {
      this.resolveSelectedLink();
    },

    search() {
      // Highlight the top result while searching. An empty search clears the
      // highlight so Enter keeps the current selection.
      this.highlightedIndex = this.search.trim() ? 0 : -1;
    },
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  },

  methods: {
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.close();
      }
    },

    typeLabel(type) {
      const labels = {
        page: 'Page',
        custom: 'Link',
        shoppable: 'Shoppable',
      };

      return labels[type] || 'Link';
    },

    typeTagStyle(type) {
      return TRACKING_LINK_TYPE_STYLES[type] || FALLBACK_TYPE_STYLE;
    },

    formatShortUrl(link) {
      if (!link?.rid) {
        return '';
      }

      const baseUrl = process.env.TGB_IO_URL || 'https://tgb.io';
      const host = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '').split('/')[0];

      return `${host}/t/${link.rid}`;
    },

    stripProtocol(url) {
      return (url || '').replace(/^https?:\/\//i, '');
    },

    async loadLinks() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.hasError = false;

      try {
        const resp = await getTrackingLinks({
          limit: 100,
          sortBy: 'updatedAt',
          sortDir: 'desc',
          // Shoppable links are managed by the Shoppable flow and never belong
          // in the picker. Also filter client-side so older APIs still behave.
          excludeTypes: 'shoppable',
          ...(this.type ? { type: this.type } : {}),
        });

        this.links = (resp?.links || []).filter((link) => link.type !== 'shoppable');
        this.loaded = true;

        await this.resolveSelectedLink();
      } catch (err) {
        console.error('Failed to load tracking links:', err);
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * The list is paginated, so a selected link may not be in the loaded page.
     * Fetch it by rid so the control can render the current selection.
     */
    async resolveSelectedLink() {
      if (!this.modelValue || this.links.some((link) => link.rid === this.modelValue)) {
        return;
      }

      try {
        const link = await getTrackingLinkByRid(this.modelValue);

        if (link?.rid) {
          this.links = [link, ...this.links];
        }
      } catch (err) {
        console.error('Failed to resolve selected tracking link:', err);
      }
    },

    toggle() {
      if (this.disabled) {
        return;
      }

      if (this.isOpen) {
        this.close();
        return;
      }

      this.isOpen = true;

      if (!this.loaded && !this.isLoading) {
        this.loadLinks();
      }

      this.$nextTick(() => this.$refs.searchInput?.focus());
    },

    onArrowDown() {
      if (!this.visibleLinks.length) {
        return;
      }

      this.highlightedIndex = this.highlightedIndex >= this.visibleLinks.length - 1
        ? 0
        : this.highlightedIndex + 1;

      this.scrollHighlightedIntoView();
    },

    onArrowUp() {
      if (!this.visibleLinks.length) {
        return;
      }

      this.highlightedIndex = this.highlightedIndex <= 0
        ? this.visibleLinks.length - 1
        : this.highlightedIndex - 1;

      this.scrollHighlightedIntoView();
    },

    onEnter() {
      // Arrow navigation (or an active search) selects the highlighted option,
      // which defaults to the top result while typing.
      if (this.highlightedLink) {
        this.selectLink(this.highlightedLink, true);
        return;
      }

      // Search with no matches: stay open so the user can adjust.
      if (this.search.trim()) {
        return;
      }

      // Empty search, no navigation: keep the current selection.
      this.close(true);
    },

    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const option = this.$refs.menu?.querySelector('.tracking-link-picker__option--highlighted');

        option?.scrollIntoView({ block: 'nearest' });
      });
    },

    selectLink(link, restoreFocus = false) {
      this.$emit('update:modelValue', link.rid);
      this.$emit('select', link);
      this.close(restoreFocus);
    },

    close(restoreFocus = false) {
      this.isOpen = false;
      this.search = '';
      this.highlightedIndex = -1;

      if (restoreFocus) {
        this.$nextTick(() => this.$refs.control?.focus());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tracking-link-picker {
  position: relative;
  margin-bottom: 0;

  &__control {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    width: 100%;
    min-height: 2.5rem;
    padding: 0.375rem;
    font-size: variables.$text-sm;
    color: variables.$color-on-surface-high;
    background: variables.$color-12dp;
    border: 0.0625rem solid transparent;
    border-radius: 0.125rem;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(.tracking-link-picker__control--disabled) {
      background: variables.$color-16dp;
      border-color: variables.$color-24dp;
    }

    &--open {
      background: variables.$color-24dp;
      border-color: rgba(variables.$color-primary, 0.4);
    }

    &:focus-visible {
      background: variables.$color-24dp;
      border-color: rgba(variables.$color-primary, 0.4);
      outline: none;
    }

    &--error {
      border-color: variables.$color-error;
      box-shadow: 0 0 0 3px rgba(variables.$color-error, 0.2);
    }

    &--disabled {
      color: variables.$color-on-surface-disabled;
      background: variables.$color-surface-disabled;
      cursor: not-allowed;
    }
  }

  &__icon {
    flex-shrink: 0;
    color: variables.$color-on-surface-medium;

    &--link {
      color: variables.$color-primary;
    }
  }

  &__placeholder {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 0.8125rem;
    color: variables.$color-on-surface-medium;

    &--error {
      color: variables.$color-error;
    }
  }

  &__selected {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;
  }

  &__title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 500;
    color: variables.$color-on-surface-high;
  }

  &__subtitle {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: monospace;
    font-size: 0.75rem;
    color: variables.$color-on-surface-low;
  }

  &__option-destination {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    font-size: 0.75rem;
    color: variables.$color-on-surface-low;
  }

  &__tag {
    flex-shrink: 0;
    padding: 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1.1;
    border-radius: 624.9375rem;
  }

  &__chevron {
    flex-shrink: 0;
    color: variables.$color-on-surface-medium;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__spinner {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    border: 0.125rem solid rgba(variables.$color-primary, 0.3);
    border-top-color: variables.$color-primary;
    border-radius: 50%;
    animation: tracking-link-picker-spin 0.6s linear infinite;
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin: 0.375rem 0 0;
    font-size: 0.8125rem;
    color: variables.$color-error;
  }

  &__menu {
    position: absolute;
    z-index: 20;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    max-height: 20rem;
    overflow: hidden;
    background: variables.$color-04dp;
    border: 0.0625rem solid variables.$color-12dp;
    border-radius: 0.125rem;
    box-shadow: 0 0.5rem 1.5rem rgba(#000000, 0.35);
  }

  &__search-header {
    flex-shrink: 0;
    padding: 0.375rem;
    background: variables.$color-04dp;
  }

  &__results {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  &__search-wrapper {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    min-height: 2rem;
    padding: 0.25rem 0.375rem;
    background: variables.$color-12dp;
    border: 0.0625rem solid variables.$color-16dp;
    border-radius: 0.125rem;
  }

  &__search-icon {
    color: variables.$color-on-surface-medium;
  }

  &__search {
    flex: 1;
    min-width: 0;
    padding: 0;
    font-size: 0.8125rem;
    color: variables.$color-on-surface-high;
    background: transparent;
    border: none;
    outline: none;

    &:focus {
      background: transparent;
      border: none;
      outline: none;
      box-shadow: none;
    }

    &::placeholder {
      color: variables.$color-on-surface-medium;
    }
  }

  &__group-label {
    padding: 0.375rem 0.5rem 0.1875rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: variables.$color-on-surface-low;
  }

  &__group-divider {
    height: 0;
    margin: 0.375rem 0;
    border: 0;
    border-top: 0.0625rem solid variables.$color-12dp;
  }

  &__option {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    width: 100%;
    padding: 0.375rem 0.5rem;
    text-align: left;
    background: transparent;
    border: none;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: rgba(variables.$color-on-surface, 0.04);
    }

    &--highlighted {
      background-color: rgba(variables.$color-primary, 0.16);
    }
  }

  &__option-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-width: 0;
  }

  &__state {
    padding: 0.625rem 0.5rem;
    font-size: 0.875rem;
    color: variables.$color-on-surface-medium;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1rem;
    text-align: center;
  }

  &__empty-icon {
    font-size: 1.5rem;
    color: variables.$color-on-surface-medium;
  }

  &__empty-title {
    margin: 0;
    font-weight: 500;
    color: variables.$color-on-surface-high;
  }

  &__empty-subtitle {
    margin: 0;
    font-size: 0.8125rem;
    color: variables.$color-on-surface-medium;
  }

}

@keyframes tracking-link-picker-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
